/** Site settings: general, theme, menus, SEO, store, shipping, taxes, redirects. Route /sites/:id/settings/:tab? */
import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
    AutoComplete, Button, Card, Checkbox, Collapse, ColorPicker, Divider, Form, Input, Modal, Popconfirm, Segmented, Select, Slider, Space, Switch, Tabs, Typography, message,
} from 'antd';
import { ArrowDownOutlined, ArrowUpOutlined, DeleteOutlined, PlusOutlined, SaveOutlined } from '@ant-design/icons';
import { useTranslation } from '../../../shared/i18n';
import { userPath } from '../../../shared/config/paths';
import { listOf, sitePath, toastErr, useSite } from './common';
import { BusinessTab, IntegrationsTab, RedirectsTab, useSaveSite } from './SettingsSite';
import { sb, QUIET } from './api';
import { DEFAULT_THEME, FONT_STACKS } from './editor/engine';
import ShopSettings from './ShopSettings';
import Shipping from './Shipping';
import Taxes from './Taxes';
import ShopExtSettings from './ShopExtSettings';
import ShopLegal from './ShopLegal';
import ShopFeeds from './ShopFeeds';
import ShopExports from './ShopExports';
import ShopDevelopers from './ShopDevelopers';
import ShopImports from './ShopImports';
import ShopNotifications from './ShopNotifications';
import ShopCustomFields from './ShopCustomFields';

const { Text } = Typography;
const langName = (code, ui) => {
    try { return new Intl.DisplayNames([ui || 'en'], { type: 'language' }).of(code) || code; } catch (_) { return code; }
};
const langOptions = (ui) => LANGS.map((x) => ({ value: x, label: `${langName(x, ui)} · ${x}` }));
const LANGS = ['en', 'zh-CN', 'zh-TW', 'ja', 'ko', 'es', 'fr', 'de', 'it', 'pt', 'ru', 'ms', 'id', 'th', 'vi', 'ar', 'tr', 'nl', 'pl'];

const BlogSettings = () => {
    const { t } = useTranslation();
    const { site } = useSite();
    const { save, saving } = useSaveSite();
    const [form] = Form.useForm();
    useEffect(() => {
        const s = site.settings || {};
        form.setFieldsValue({ comments: s.blog?.comments || 'moderated', rss_full: !!s.blog?.rss_full, entity: s.entity || 'organization', social: (s.social || []).join('\n') });
    }, [site, form]);
    const submit = async () => {
        const v = form.getFieldsValue();
        await save({ settings: { blog: { comments: v.comments, rss_full: !!v.rss_full }, entity: v.entity, social: String(v.social || '').split('\n').map((x) => x.trim()).filter(Boolean) } });
    };
    return (
        <Form form={form} layout="vertical" style={{ maxWidth: 560 }} onFinish={submit}>
            <Form.Item name="comments" label={t('Comments')}>
                <Segmented options={[{ value: 'off', label: t('Off') }, { value: 'moderated', label: t('Approve first') }, { value: 'open', label: t('Open') }]} />
            </Form.Item>
            <Form.Item name="rss_full" valuePropName="checked"><Checkbox>{t('Full posts in the RSS feed')}</Checkbox></Form.Item>
            <Form.Item name="entity" label={t('This site represents')}>
                <Segmented options={[{ value: 'organization', label: t('An organization') }, { value: 'person', label: t('A person') }]} />
            </Form.Item>
            <Form.Item name="social" label={t('Social profile links (one per line)')}><Input.TextArea autoSize={{ minRows: 3 }} placeholder="https://" /></Form.Item>
            <Button type="primary" htmlType="submit" loading={saving} icon={<SaveOutlined />}>{t('Save')}</Button>
        </Form>
    );
};

const General = () => {
    const { t, i18n } = useTranslation();
    const navigate = useNavigate();
    const { site, api } = useSite();
    const { save, saving } = useSaveSite();
    const [form] = Form.useForm();
    const [delOpen, setDelOpen] = useState(false);
    const [confirm, setConfirm] = useState('');
    useEffect(() => {
        const s = site.settings || {};
        form.setFieldsValue({ name: site.name, kind: site.kind || 'business', default_lang: site.default_lang || 'en', languages: s.languages || [], favicon: s.favicon || '', logo: s.logo || '', ga4: s.analytics?.ga4 || '',
            access_enabled: !!s.access?.enabled, access_message: s.access?.message || '', password: '' });
    }, [site, form]);
    const hasPassword = !!site.settings?.access?.has_password;
    const accessOn = Form.useWatch('access_enabled', form);
    const submit = async () => {
        const v = await form.validateFields().catch(() => null);
        if (!v) return;
        const settings = { languages: v.languages, favicon: v.favicon, logo: v.logo, analytics: { ...(site.settings?.analytics || {}), ga4: v.ga4 },
            access: { enabled: !!v.access_enabled, message: (v.access_message || '').trim() } };
        if (v.password) settings.password = v.password;
        if (await save({ name: v.name, kind: v.kind, default_lang: v.default_lang, settings })) form.setFieldsValue({ password: '' });
    };
    const remove = async () => {
        try { await api.del(''); message.success(t('Site deleted')); navigate(userPath('/sites')); } catch (e) { toastErr(e); }
    };
    return (
        <>
            <Form form={form} layout="vertical" requiredMark={false} style={{ maxWidth: 560 }}>
                <Form.Item name="name" label={t('Site name')} rules={[{ required: true, whitespace: true }]}><Input maxLength={120} /></Form.Item>
                <Form.Item name="kind" label={t('Site type')}>
                    <Select style={{ width: 200 }} options={[{ value: 'business', label: t('Business site') }, { value: 'shop', label: t('Online store') }, { value: 'blog', label: t('Blog') }]} />
                </Form.Item>
                <Space wrap align="start">
                    <Form.Item name="default_lang" label={t('Language')}><Select style={{ width: 200 }} showSearch optionFilterProp="label" options={langOptions(i18n?.language)} /></Form.Item>
                    <Form.Item name="languages" label={t('Other languages')} tooltip={t('Pages in these languages get hreflang links')}>
                        <Select mode="tags" style={{ minWidth: 260 }} optionFilterProp="label" options={langOptions(i18n?.language)} />
                    </Form.Item>
                </Space>
                <Form.Item name="logo" label={t('Logo URL')}><Input placeholder="https://…" /></Form.Item>
                <Form.Item name="favicon" label={t('Favicon URL')}><Input placeholder="https://…" /></Form.Item>
                <Form.Item name="ga4" label="Google Analytics 4" rules={[{ pattern: /^(G-[A-Z0-9]{4,16})?$/, message: 'G-XXXXXXX' }]}><Input placeholder="G-XXXXXXX" style={{ width: 220 }} /></Form.Item>
                <Collapse ghost size="small" style={{ margin: '0 -16px 16px' }} defaultActiveKey={site.settings?.access?.enabled ? ['access'] : []} items={[{
                    key: 'access', forceRender: true, label: t('Password protection'),
                    children: (
                        <>
                            <Form.Item name="access_enabled" valuePropName="checked" label={t('Require a password to view the site')} tooltip={t('Takes effect after the next publish')}>
                                <Switch />
                            </Form.Item>
                            <Form.Item name="password" label={t('Password')}
                                rules={[{ min: 4, max: 100, message: t('4 to 100 characters') }, { required: !!accessOn && !hasPassword, message: t('Set a password') }]}>
                                <Input.Password autoComplete="new-password" placeholder={hasPassword ? t('Leave empty to keep the current password') : ''} style={{ width: 320 }} />
                            </Form.Item>
                            {hasPassword && (
                                <Popconfirm title={t('Remove the password?')} okButtonProps={{ danger: true }}
                                    onConfirm={async () => { if (await save({ settings: { access: { enabled: false, message: site.settings?.access?.message || '' }, password: '-' } })) form.setFieldsValue({ access_enabled: false, password: '' }); }}>
                                    <Button type="link" danger size="small" style={{ padding: 0, marginTop: -16, marginBottom: 16 }}>{t('Remove password')}</Button>
                                </Popconfirm>
                            )}
                            <Form.Item name="access_message" label={t('Message for visitors (optional)')}><Input maxLength={200} /></Form.Item>
                        </>
                    ),
                }]} />
                <Button type="primary" icon={<SaveOutlined />} loading={saving} onClick={submit}>{t('Save')}</Button>
            </Form>
            <Divider />
            <Button danger onClick={() => setDelOpen(true)}>{t('Delete site')}</Button>
            <Modal open={delOpen} title={t('Delete site')} onCancel={() => setDelOpen(false)} onOk={remove} okButtonProps={{ danger: true, disabled: confirm !== site.slug }} okText={t('Delete')}>
                <Text>{t('Type {{slug}} to confirm.', { slug: site.slug })}</Text>
                <Input style={{ marginTop: 8 }} value={confirm} onChange={(e) => setConfirm(e.target.value)} placeholder={site.slug} />
            </Modal>
        </>
    );
};

const FALLBACK_CSS = { sans: 'sans-serif', serif: 'serif', mono: 'monospace', display: 'sans-serif' };
const SPACING_PAD = { compact: 12, normal: 20, airy: 32 };

const ThemeTab = () => {
    const { t } = useTranslation();
    const { site } = useSite();
    const { save, saving } = useSaveSite();
    const [th, setTh] = useState({ ...DEFAULT_THEME, ...(site.theme || {}) });
    const [fonts, setFonts] = useState([]);
    useEffect(() => { setTh({ ...DEFAULT_THEME, ...(site.theme || {}) }); }, [site.theme]);
    useEffect(() => { sb.get('fonts', null, QUIET).then((d) => setFonts(listOf(d))).catch(() => {}); }, []);
    const fontOptions = fonts.map((f) => ({ value: f.slug, label: f.family }));
    const stack = (slug) => {
        const f = fonts.find((x) => x.slug === slug);
        return f ? `"${f.family}",${FALLBACK_CSS[f.fallback] || 'sans-serif'}` : FONT_STACKS[th.font];
    };
    // optional colors may be cleared: '' = derived from the main colors
    const color = (k, label, optional) => (
        <Space style={{ width: 280, justifyContent: 'space-between' }}>
            <Text>{label}</Text>
            <ColorPicker value={th[k] || ''} showText disabledAlpha allowClear={!!optional} onChange={(c) => setTh({ ...th, [k]: c.cleared ? '' : c.toHexString() })} />
        </Space>
    );
    const fontSelect = (k, label) => (
        <div style={{ width: 360 }}>
            <Text>{label}</Text>
            <Select style={{ width: '100%', marginTop: 6 }} allowClear showSearch optionFilterProp="label" placeholder={t('Default')} value={th[k] || undefined} options={fontOptions}
                optionRender={(o) => <span style={{ fontFamily: stack(o.value) }}>{o.label}</span>} onChange={(v) => setTh({ ...th, [k]: v || '' })} />
        </div>
    );
    const buttonStyle = th.button_style || 'solid';
    const radius = buttonStyle === 'pill' ? 999 : Number(th.radius) || 0;
    return (
        <div style={{ maxWidth: 560 }}>
            <Space direction="vertical" size={12}>
                {color('accent', t('Accent'))}
                {color('secondary', t('Secondary color'), true)}
                {color('background', t('Background'))}
                {color('surface', t('Surface color'), true)}
                {color('text', t('Text'))}
                {color('muted', t('Muted text'), true)}
                <div>
                    <Text>{t('Font')}</Text>
                    <Segmented block style={{ marginTop: 6, width: 360 }} value={th.font} onChange={(font) => setTh({ ...th, font })}
                        options={Object.keys(FONT_STACKS).map((k) => ({ value: k, label: <span style={{ fontFamily: FONT_STACKS[k] }}>{t({ system: 'Sans', serif: 'Serif', rounded: 'Rounded', mono: 'Mono' }[k])}</span> }))} />
                </div>
                {fontSelect('heading_font', t('Heading font'))}
                {fontSelect('body_font', t('Body font'))}
                <div style={{ width: 360 }}>
                    <Text>{t('Corner radius')}</Text>
                    <Slider min={0} max={40} value={Number(th.radius) || 0} onChange={(r) => setTh({ ...th, radius: r })} />
                </div>
                <div>
                    <Text>{t('Button style')}</Text>
                    <Segmented block style={{ marginTop: 6, width: 360 }} value={buttonStyle} onChange={(button_style) => setTh({ ...th, button_style })}
                        options={[{ value: 'solid', label: t('Solid') }, { value: 'outline', label: t('Outlined') }, { value: 'pill', label: t('Pill') }]} />
                </div>
                <div>
                    <Text>{t('Spacing')}</Text>
                    <Segmented block style={{ marginTop: 6, width: 360 }} value={th.spacing || 'normal'} onChange={(spacing) => setTh({ ...th, spacing })}
                        options={[{ value: 'compact', label: t('Tight') }, { value: 'normal', label: t('Normal') }, { value: 'airy', label: t('Airy') }]} />
                </div>
                {/* sample */}
                <div style={{ background: th.surface || th.background, color: th.text, fontFamily: stack(th.body_font), border: '1px solid #eee', borderRadius: 8, padding: SPACING_PAD[th.spacing] || 20, width: 360 }}>
                    <div style={{ fontSize: 22, fontWeight: 700, marginBottom: 6, fontFamily: stack(th.heading_font) }}>{site.name}</div>
                    <div style={{ color: th.muted || th.text, opacity: th.muted ? 1 : 0.8, marginBottom: 12 }}>{t('Preview of your theme')}</div>
                    <span style={buttonStyle === 'outline'
                        ? { border: `1px solid ${th.accent}`, color: th.accent, padding: '8px 16px', borderRadius: radius, display: 'inline-block' }
                        : { background: th.accent, color: '#fff', padding: '8px 16px', borderRadius: radius, display: 'inline-block' }}>{t('Button')}</span>
                </div>
                <Button type="primary" icon={<SaveOutlined />} loading={saving} onClick={() => save({ theme: th })}>{t('Save')}</Button>
            </Space>
        </div>
    );
};

const MenuEditor = ({ items, onChange, pageOptions, depth = 1 }) => {
    const { t } = useTranslation();
    const list = items || [];
    const set = (i, patch) => onChange(list.map((x, j) => (j === i ? { ...x, ...patch } : x)));
    const mv = (i, d) => { const n = [...list]; const [x] = n.splice(i, 1); n.splice(i + d, 0, x); onChange(n); };
    return (
        <div style={{ paddingLeft: depth > 1 ? 24 : 0 }}>
            {list.map((it, i) => (
                <div key={i}>
                    <Space.Compact style={{ width: '100%', marginBottom: 6 }}>
                        <Input style={{ width: '35%' }} placeholder={t('Label')} value={it.label} onChange={(e) => set(i, { label: e.target.value })} />
                        <AutoComplete style={{ flex: 1 }} options={pageOptions} value={it.url} onChange={(url) => set(i, { url })} placeholder={t('URL or page path')}
                            filterOption={(input, o) => String(o.value).toLowerCase().includes(String(input).toLowerCase())} />
                        <Button icon={<ArrowUpOutlined />} disabled={i === 0} onClick={() => mv(i, -1)} />
                        <Button icon={<ArrowDownOutlined />} disabled={i === list.length - 1} onClick={() => mv(i, 1)} />
                        {depth < 2 && <Button icon={<PlusOutlined />} title={t('Add sub-item')} onClick={() => set(i, { children: [...(it.children || []), { label: '', url: '' }] })} />}
                        <Button danger icon={<DeleteOutlined />} onClick={() => onChange(list.filter((_, j) => j !== i))} />
                    </Space.Compact>
                    {depth < 2 && (it.children || []).length > 0 && (
                        <MenuEditor items={it.children} depth={depth + 1} pageOptions={pageOptions} onChange={(children) => set(i, { children })} />
                    )}
                </div>
            ))}
            {depth === 1 && <Button type="dashed" icon={<PlusOutlined />} onClick={() => onChange([...list, { label: '', url: '', children: [] }])}>{t('Add link')}</Button>}
        </div>
    );
};

const Menus = () => {
    const { t } = useTranslation();
    const { site, api } = useSite();
    const { save, saving } = useSaveSite();
    const [menus, setMenus] = useState({ header: [], footer: [] });
    const [pages, setPages] = useState([]);
    useEffect(() => { setMenus({ header: [], footer: [], ...(site.settings?.menus || {}) }); }, [site.settings]);
    useEffect(() => { api.get('/pages').then((d) => setPages(listOf(d))).catch(() => {}); }, [api]);
    const pageOptions = useMemo(() => pages.filter((p) => !String(p.path).includes(':')).map((p) => ({ value: p.path, label: `${p.path} · ${p.title || ''}` })), [pages]);
    return (
        <div style={{ maxWidth: 760 }}>
            <Text strong>{t('Header')}</Text>
            <div style={{ margin: '8px 0 20px' }}><MenuEditor items={menus.header} pageOptions={pageOptions} onChange={(header) => setMenus({ ...menus, header })} /></div>
            <Text strong>{t('Footer')}</Text>
            <div style={{ margin: '8px 0 20px' }}><MenuEditor items={menus.footer} pageOptions={pageOptions} onChange={(footer) => setMenus({ ...menus, footer })} /></div>
            <Button type="primary" icon={<SaveOutlined />} loading={saving} onClick={() => save({ settings: { menus } })}>{t('Save')}</Button>
        </div>
    );
};

const Seo = () => {
    const { t } = useTranslation();
    const { site } = useSite();
    const { save, saving } = useSaveSite();
    const [tt, setTt] = useState('');
    const [org, setOrg] = useState(false);
    useEffect(() => { setTt(site.settings?.title_template || ''); setOrg(!!site.settings?.organization); }, [site.settings]);
    const sample = (tt || `%s · ${site.name}`).replace('%s', t('About us'));
    return (
        <div style={{ maxWidth: 560 }}>
            <Form layout="vertical">
                <Form.Item label={t('Title template')} tooltip={t('%s is replaced by the page title')}>
                    <Input value={tt} onChange={(e) => setTt(e.target.value)} placeholder={`%s · ${site.name}`} maxLength={120} />
                </Form.Item>
                <div style={{ border: '1px solid #eee', borderRadius: 8, padding: 12, marginBottom: 16 }}>
                    <div style={{ color: '#1a0dab', fontSize: 18 }}>{sample}</div>
                    <div style={{ color: '#006621', fontSize: 13 }}>{site.url}/about</div>
                </div>
                <Form.Item><Checkbox checked={org} onChange={(e) => setOrg(e.target.checked)}>{t('Organization structured data on the home page')}</Checkbox></Form.Item>
                <Text type="secondary" style={{ display: 'block', marginBottom: 16, fontSize: 12 }}>{t('Page titles and descriptions are set per page in the editor.')}</Text>
                <Button type="primary" icon={<SaveOutlined />} loading={saving} onClick={() => save({ settings: { title_template: tt, organization: org } })}>{t('Save')}</Button>
            </Form>
        </div>
    );
};

const TABS = [
    { key: 'general', label: 'General', el: <General /> },
    { key: 'business', label: 'Business', el: <BusinessTab /> },
    { key: 'theme', label: 'Theme', el: <ThemeTab /> },
    { key: 'menus', label: 'Menus', el: <Menus /> },
    { key: 'seo', label: 'SEO', el: <Seo /> },
    { key: 'blog', label: 'Blog', el: <BlogSettings /> },
    { key: 'integrations', label: 'Integrations', el: <IntegrationsTab /> },
    { key: 'store', label: 'Store', el: <ShopSettings /> },
    { key: 'shipping', label: 'Shipping & delivery', el: <Shipping /> },
    { key: 'taxes', label: 'Taxes', el: <Taxes /> },
    { key: 'extensions', label: 'Store extras', el: <ShopExtSettings /> },
    { key: 'notifications', label: 'Notifications', el: <ShopNotifications /> },
    { key: 'legal', label: 'Legal', el: <ShopLegal /> },
    { key: 'feeds', label: 'Product feeds', el: <ShopFeeds /> },
    { key: 'custom-fields', label: 'Custom fields', el: <ShopCustomFields /> },
    { key: 'exports', label: 'Exports', el: <ShopExports /> },
    { key: 'imports', label: 'Imports', el: <ShopImports /> },
    { key: 'developers', label: 'Developers', el: <ShopDevelopers /> },
    { key: 'redirects', label: 'Redirects', el: <RedirectsTab /> },
];

const STORE_TABS = new Set(['store', 'shipping', 'taxes', 'extensions', 'notifications', 'legal', 'feeds', 'custom-fields', 'exports', 'imports', 'developers']);

const Settings = () => {
    const { t } = useTranslation();
    const { tab = 'general' } = useParams();
    const navigate = useNavigate();
    const { siteId, site } = useSite();
    const tabs = TABS.filter((x) => site?.kind === 'shop' || !STORE_TABS.has(x.key) || x.key === tab);
    return (
        <Card size="small">
            <Tabs activeKey={tab} destroyInactiveTabPane onChange={(k) => navigate(sitePath(siteId, `settings/${k}`))}
                items={tabs.map((x) => ({ key: x.key, label: t(x.label), children: x.el }))} />
        </Card>
    );
};

export default Settings;
